/**
 * @fileoverview Defines the `SignUp` component, a form for user account creation. It uses the `useAuth` hook and functions like `signUpWithEmail`, `signInWithGitHub`, and `signInWithGoogle` for registration. Upon successful signup, the user is redirected to either the subscription flow or the account page.
 */

import React, { useCallback, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { updateMarketingConsent } from '../../firebase/functions'
import { useAuth } from '../../hooks/useAuth'
import '../../styles/components/Auth.css'
import { GitHubFilledIcon, GoogleIcon } from '../Icons'

export function SignUp() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [marketingConsent, setMarketingConsent] = useState(true)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signUpWithEmail, signInWithGitHub, signInWithGoogle, error: authError } = useAuth()
  const tier = searchParams.get('tier')

  // Unified success handler for all signup methods
  const handleSignupSuccess = useCallback(async () => {
    try {
      await updateMarketingConsent({ marketingConsent })
    } catch (consentError) {
      console.error('Failed to update marketing consent:', consentError)
    }

    if (tier === 'basic') {
      navigate('/subscribe?tier=basic')
    } else {
      navigate('/dashboard')
    }
  }, [marketingConsent, tier, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    // Validate input
    if (!email || !password) {
      setValidationError('Please fill in all fields')
      return
    }

    if (password !== confirmPassword) {
      setValidationError('Passwords do not match')
      return
    }

    if (password.length < 6) {
      setValidationError('Password must be at least 6 characters')
      return
    }

    setIsSubmitting(true)

    try {
      const success = await signUpWithEmail(email, password)

      if (!success) {
        setValidationError('Failed to create account. Please try again.')
      } else {
        await handleSignupSuccess()
      }
    } catch (err: any) {
      console.error('Signup error:', err)
      setValidationError(err.message || 'An error occurred during sign up')
      Sentry.captureException(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGitHubSignUp = async () => {
    setIsSubmitting(true)

    try {
      const success = await signInWithGitHub()
      if (success) {
        await handleSignupSuccess()
      }
    } catch (err: any) {
      console.error('GitHub signup error:', err)
      setValidationError(err.message || 'An error occurred during GitHub sign up')
      Sentry.captureException(err)
    }
    setIsSubmitting(false)
  }

  const handleGoogleSignUp = async () => {
    setIsSubmitting(true)

    try {
      const success = await signInWithGoogle()
      if (success) {
        await handleSignupSuccess()
      }
    } catch (err: any) {
      console.error('Google signup error:', err)
      setValidationError(err.message || 'An error occurred during Google sign up')
      Sentry.captureException(err)
    }
    setIsSubmitting(false)
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Link to="/" className="home-link">
          Ref.
        </Link>
        <h2 className="auth-title">Create an account</h2>

        {tier === 'basic' && (
          <div className="signup-tier-info">
            <p>
              You're signing up for the <strong>Basic Plan</strong> ($9/month). After creating your
              account, you'll be redirected to complete your subscription.
            </p>
          </div>
        )}

        {(authError || validationError) && (
          <div className="error-message">{authError || validationError}</div>
        )}

        <div className="social-auth-buttons">
          <button
            onClick={handleGitHubSignUp}
            className="cta-button secondary social-button"
            disabled={isSubmitting}
            aria-label="Sign up with GitHub"
          >
            <GitHubFilledIcon size={16} className="button-icon" />
            <span>{isSubmitting ? 'Creating Account...' : 'Sign up with GitHub'}</span>
          </button>

          <button
            onClick={handleGoogleSignUp}
            className="cta-button secondary social-button"
            disabled={isSubmitting}
            aria-label="Sign up with Google"
          >
            <GoogleIcon size={16} className="button-icon" />
            <span>{isSubmitting ? 'Creating Account...' : 'Sign up with Google'}</span>
          </button>
        </div>

        <div className="auth-divider">
          <span>OR</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group checkbox">
            <input
              type="checkbox"
              id="marketingConsent"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              disabled={isSubmitting}
              aria-label="I agree to receive updates about Ref. Unsubscribe any time."
            />
            <label htmlFor="marketingConsent">
              I agree to receive updates about Ref. Unsubscribe any time.
            </label>
          </div>

          <button type="submit" className="cta-button primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating Account...' : 'Sign up'}
          </button>
        </form>

        <div className="auth-links">
          Already have an account?
          <Link to="/login" className="link" style={{ marginLeft: '1rem' }}>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
